<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router';
import SimpleLayoutWithLogo from '@@/packages/shared/components/layouts/SimpleLayoutWithLogo.vue';
import { useCurrentOrg, useOrgUsers } from '@/modules/current-org/api';
import { useCurrentOrgRoles } from '@/modules/roles/api';
import Permission from '@/modules/current-user/permissions/permissions';
import { useMode } from '@/modules/mode/composables';
import { useAvailableModes } from '@/modules/mode/composables/useAvailableModes';

const { availableModes } = useAvailableModes();

const route = useRoute();
const { currentOrg } = useCurrentOrg();
const permissionsKeys = computed(() => {
  const permissions = (route.meta.permissions as Permission[] | undefined) ?? [];
  const permissionEntries = Object.entries(Permission);

  // route.meta is untyped, so the lookup can miss. An unmatched permission would
  // otherwise reach the template as a raw translation key, and count towards the
  // list's own emptiness check.
  return permissions
    .filter((permission) => {
      if (permission.startsWith('clients') && !currentOrg.value?.isClient) return false;
      if (permission.startsWith('suppliers') && !currentOrg.value?.isSupplier) return false;
      if (permission.startsWith('federated') && !currentOrg.value?.isFederated) return false;
      return true;
    })
    .map((permission) => permissionEntries.find(([, value]) => value === permission)?.[0])
    .filter((key) => key !== undefined);
});

const { roles } = useCurrentOrgRoles();
const { allOrgUsers } = useOrgUsers();
const adminUsers = computed(() => {
  const adminRole = roles.value.find(({ name }) => ['admin', 'federated_admin'].includes(name));
  return Object.values(allOrgUsers.value).filter(({ roleId }) => roleId === adminRole?.id);
});

const router = useRouter();
const { setMode } = useMode();
function goBack() {
  const target = availableModes.value.find((mode) => mode.hasPermission);
  if (target) {
    setMode(target.mode);
    router.push(target.route);
    return;
  }
  router.push('/settings');
}
</script>

<template>
  <SimpleLayoutWithLogo>
    <div class="flex flex-col gap-3x rounded-sm border p-8x text-base">
      <BaseHeading size="md" :level="2">
        {{ $t('errorScreens.noPermissions.title') }}
      </BaseHeading>
      <!-- A route can be refused on its mode alone, and the list is filtered by org
        type, so there is not always a permission to name. -->
      <template v-if="permissionsKeys.length">
        <p class="m-0">{{ $t('errorScreens.noPermissions.body1', permissionsKeys.length) }}</p>

        <blockquote class="m-0 space-y-1x rounded-sm border p-6x font-medium">
          <div v-for="permission in permissionsKeys" :key="permission">
            {{ $t(`roles.permissions.${permission}.title`) }}
          </div>
        </blockquote>
      </template>

      <p class="m-0">{{ $t('errorScreens.noPermissions.body2') }}</p>

      <div class="flex flex-wrap gap-2x">
        <BaseLabel v-for="user in adminUsers" :key="user.id">{{ user.fullName }}</BaseLabel>
      </div>

      <div class="flex justify-end">
        <BaseButton type="button" variant="filled" @click="goBack">
          {{ $t('errorScreens.noPermissions.backToPlatform') }}
        </BaseButton>
      </div>
    </div>
  </SimpleLayoutWithLogo>
</template>
